import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { cartService, productService, publicCatalogService, getClinicToken } from '../../services';
import { ApiError, Cart, CartConflict, CartItem, ClinicProduct } from '../../types';
import { CLINIC_STORAGE_KEYS } from '../../constants/buyer-portal';
import { isWeb } from '../../utilities/platform';
import {
  BuyerIdentity,
  resolveProductPrice,
} from '../../components/clinic-business/productStatus';

const EMPTY_CART: Cart = { items: [], subtotal: 0, gstTotal: 0, total: 0 };

export type CartMode = 'guest' | 'authed';

type GuestLine = { productId: string; quantity: number };

const round2 = (n: number): number => Math.round(n * 100) / 100;

const readGuestLines = (): GuestLine[] => {
  if (!isWeb) return [];
  try {
    const raw = localStorage.getItem(CLINIC_STORAGE_KEYS.GUEST_CART);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((l): l is GuestLine =>
        !!l && typeof l.productId === 'string' && typeof l.quantity === 'number' && l.quantity > 0,
      )
      .map((l) => ({ productId: l.productId, quantity: l.quantity }));
  } catch {
    return [];
  }
};

const writeGuestLines = (lines: GuestLine[]): void => {
  if (!isWeb) return;
  if (lines.length === 0) localStorage.removeItem(CLINIC_STORAGE_KEYS.GUEST_CART);
  else localStorage.setItem(CLINIC_STORAGE_KEYS.GUEST_CART, JSON.stringify(lines));
};

const effectivePrice = (
  product: ClinicProduct,
  identity: BuyerIdentity,
): number => {
  const resolved = resolveProductPrice(product, identity);
  if (typeof resolved === 'number' && !Number.isNaN(resolved)) return resolved;
  if (typeof product.salePrice === 'number' && !Number.isNaN(product.salePrice)) {
    return product.salePrice;
  }
  return 0;
};

const buildCartFromProducts = (
  lines: GuestLine[],
  productById: Map<string, ClinicProduct>,
  identity: BuyerIdentity = 'GUEST',
): Cart => {
  const items: CartItem[] = [];
  let subtotal = 0;
  let gstTotal = 0;
  for (const line of lines) {
    const product = productById.get(line.productId);
    if (!product) continue;
    const unit = effectivePrice(product, identity);
    const lineTotal = round2(unit * line.quantity);
    const gst = round2(lineTotal * (product.gstRate ?? 0));
    subtotal += lineTotal;
    gstTotal += gst;
    items.push({
      productId: product.id,
      quantity: line.quantity,
      // Overwrite the stub price so downstream UI (CartItemRow) shows the
      // identity-correct unit price instead of the raw catalogue salePrice.
      product: { ...product, salePrice: unit },
      lineTotal,
    });
  }
  subtotal = round2(subtotal);
  gstTotal = round2(gstTotal);
  return {
    items,
    subtotal,
    gstTotal,
    total: round2(subtotal + gstTotal),
  };
};

const productDetail = (id: string) =>
  getClinicToken() ? productService.detail(id) : publicCatalogService.detail(id);

const fetchProducts = async (
  productIds: string[],
): Promise<Map<string, ClinicProduct>> => {
  const map = new Map<string, ClinicProduct>();
  if (productIds.length === 0) return map;
  await Promise.all(
    productIds.map(async (id) => {
      try {
        const product = await productDetail(id);
        map.set(id, product);
      } catch {
        // product not found — skipped (deslited)
      }
    }),
  );
  return map;
};

export class CartStore {
  cart: Cart = EMPTY_CART;
  loading = false;
  mutating = false;
  validating = false;
  error: ApiError | null = null;
  conflicts: CartConflict[] = [];
  mode: CartMode = 'guest';

  /**
   * Transient single-item "Buy now" cart. When set, checkout reads from this
   * instead of the real cart so the order contains only this product, leaving
   * the user's actual cart untouched. Cleared after the order or on leaving.
   */
  buyNowCart: Cart | null = null;
  buyNowLoading = false;
  /** Set by `BuyerRootStore` so guest-cart math picks the right price. */
  private identityProvider: () => BuyerIdentity = () => 'GUEST';

  setIdentityProvider(fn: () => BuyerIdentity): void {
    this.identityProvider = fn;
  }

  constructor() {
    makeObservable(this, {
      cart: observable,
      loading: observable,
      mutating: observable,
      validating: observable,
      error: observable,
      conflicts: observable,
      mode: observable,
      buyNowCart: observable,
      buyNowLoading: observable,
      itemCount: computed,
      isEmpty: computed,
      loadBuyNow: action,
      clearBuyNow: action,
      adoptGuestLinesToServer: action,
      setMode: action,
      load: action,
      addItem: action,
      updateItem: action,
      removeItem: action,
      validate: action,
      clear: action,
      clearLocal: action,
      clearConflicts: action,
      getGuestLines: action,
    });
  }

  get itemCount(): number {
    return this.cart.items.reduce((sum, i) => sum + i.quantity, 0);
  }

  get isEmpty(): boolean {
    return this.cart.items.length === 0;
  }

  /** Units of the given product currently in the cart (0 when absent). */
  quantityForProduct(productId: string): number {
    return (
      this.cart.items.find((i) => i.productId === productId)?.quantity ?? 0
    );
  }

  private setCart(next: Cart): void {
    this.cart = next;
  }

  setMode(mode: CartMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.cart = EMPTY_CART;
    this.error = null;
    this.conflicts = [];
  }

  /** Returns guest cart lines for use in guest checkout payload. */
  getGuestLines(): GuestLine[] {
    if (this.mode !== 'guest') return [];
    return this.cart.items.map((i) => ({ productId: i.productId, quantity: i.quantity }));
  }

  /** Lines for the active checkout source (Buy now if set, else the real cart). */
  getCheckoutLines(): GuestLine[] {
    const source = this.buyNowCart ?? this.cart;
    return source.items.map((i) => ({ productId: i.productId, quantity: i.quantity }));
  }

  /**
   * Build a single-item "Buy now" cart for `productId`. Quantity defaults to the
   * product's MOQ, clamped to available stock / remaining slots. Throws if the
   * product can't be purchased so the caller can surface an error.
   */
  async loadBuyNow(productId: string, quantity?: number): Promise<void> {
    this.buyNowLoading = true;
    this.error = null;
    try {
      const product = await productDetail(productId);
      const moq = product.moq && product.moq > 1 ? product.moq : 1;
      const max =
        product.productType === 'VIRTUAL'
          ? product.virtualConfig
            ? product.virtualConfig.capacity === null
              ? 999999
              : Math.max(
                  0,
                  product.virtualConfig.capacity -
                    product.virtualConfig.soldSlots -
                    product.virtualConfig.reservedSlots,
                )
            : 0
          : product.availableStock;
      const desired = Math.max(moq, quantity ?? moq);
      const qty = Math.min(desired, max);
      if (qty <= 0) {
        const e: ApiError = { code: 'OUT_OF_STOCK', message: 'Out of stock' };
        throw e;
      }
      const productById = new Map<string, ClinicProduct>([[product.id, product]]);
      const cart = buildCartFromProducts([{ productId, quantity: qty }], productById, this.identityProvider());
      runInAction(() => {
        this.buyNowCart = cart;
      });
    } catch (err) {
      runInAction(() => {
        this.buyNowCart = null;
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.buyNowLoading = false;
      });
    }
  }

  clearBuyNow(): void {
    this.buyNowCart = null;
  }

  /**
   * Migrate the local guest cart onto the server cart after a mid-checkout
   * account registration, then switch to authed mode. Best-effort: stock
   * conflicts surface later via `validate()`. No-op for Buy-now (that order
   * uses explicit `buyNowItems`, so the real cart is irrelevant).
   */
  async adoptGuestLinesToServer(): Promise<void> {
    if (this.buyNowCart) {
      this.setMode('authed');
      return;
    }
    const lines = this.cart.items.map((i) => ({
      productId: i.productId,
      quantity: i.quantity,
    }));
    this.clearLocal();
    this.setMode('authed');
    for (const l of lines) {
      try {
        await cartService.addItem(l.productId, l.quantity);
      } catch {
        /* best-effort; out-of-stock items are dropped */
      }
    }
    await this.load();
  }

  /**
   * Backend `/buyer/cart` returns a flat DTO without `imageUrls` or
   * `virtualConfig` and with null `availableStock` for virtual products. The
   * synthesized stub in cartMapper therefore loses thumbnails for image-bearing
   * SKUs and shows virtual products as out-of-stock. Re-fetch each item's full
   * detail and merge it back so cart UI matches catalog UI.
   */
  private async hydrateAuthedCart(cart: Cart): Promise<Cart> {
    if (cart.items.length === 0) return cart;
    const products = await fetchProducts(cart.items.map((i) => i.productId));
    const identity = this.identityProvider();
    let subtotal = 0;
    let gstTotal = 0;
    const items = cart.items.map((i) => {
      const full = products.get(i.productId);
      // If the backend's unitPrice is absent / NaN, fall back to the catalogue
      // price for the buyer's identity so the cart never shows "NaN".
      const stubUnit =
        typeof i.product.salePrice === 'number' && !Number.isNaN(i.product.salePrice) && i.product.salePrice > 0
          ? i.product.salePrice
          : null;
      const unit = stubUnit ?? (full ? effectivePrice(full, identity) : 0);
      const gstRate = (full?.gstRate ?? i.product.gstRate) ?? 0;
      const lineTotal = round2(unit * i.quantity);
      subtotal += lineTotal;
      gstTotal += round2(lineTotal * gstRate);
      if (!full) {
        return {
          ...i,
          product: { ...i.product, salePrice: unit, gstRate },
          lineTotal,
        };
      }
      const isVirtual = i.product.productType === 'VIRTUAL';
      return {
        ...i,
        lineTotal,
        product: {
          ...i.product,
          // Backend cart DTO often omits names; trust the catalog detail.
          nameEn: full.nameEn || i.product.nameEn,
          nameZh: full.nameZh ?? i.product.nameZh,
          salePrice: unit,
          gstRate,
          businessPrice: full.businessPrice,
          individualPrice: full.individualPrice,
          imageUrl: full.imageUrl,
          imageUrls: full.imageUrls,
          virtualConfig: full.virtualConfig,
          brand: full.brand,
          spec: full.spec,
          unit: full.unit || i.product.unit,
          supplier: full.supplier,
          // For virtual products the cart DTO's stockStatus / availableStock
          // come back null; trust the catalog-side derived values instead.
          // For physical, the cart server-side stock is authoritative.
          stockStatus: isVirtual ? full.stockStatus : i.product.stockStatus,
          availableStock: isVirtual ? full.availableStock : i.product.availableStock,
          moq: full.moq ?? i.product.moq,
        },
      };
    });
    subtotal = round2(subtotal);
    gstTotal = round2(gstTotal);
    return {
      ...cart,
      items,
      // Backend totals may be off if it didn't send unitPrice; recompute.
      subtotal: cart.subtotal && cart.subtotal > 0 ? cart.subtotal : subtotal,
      gstTotal: cart.gstTotal && cart.gstTotal > 0 ? cart.gstTotal : gstTotal,
      total:
        cart.total && cart.total > 0
          ? cart.total
          : round2(subtotal + gstTotal),
    };
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      if (this.mode === 'guest') {
        const lines = readGuestLines();
        const products = await fetchProducts(lines.map((l) => l.productId));
        const cart = buildCartFromProducts(lines, products, this.identityProvider());
        const validLines = lines.filter((l) => products.has(l.productId));
        if (validLines.length !== lines.length) writeGuestLines(validLines);
        runInAction(() => this.setCart(cart));
      } else {
        const cart = await this.hydrateAuthedCart(await cartService.get());
        runInAction(() => this.setCart(cart));
      }
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  private async guestMutate(
    update: (lines: GuestLine[]) => GuestLine[] | Promise<GuestLine[]>,
  ): Promise<void> {
    const next = await update(readGuestLines());
    writeGuestLines(next);
    const products = await fetchProducts(next.map((l) => l.productId));
    const cart = buildCartFromProducts(next, products, this.identityProvider());
    runInAction(() => this.setCart(cart));
  }

  async addItem(productId: string, quantity = 1): Promise<void> {
    this.mutating = true;
    this.error = null;
    try {
      if (this.mode === 'guest') {
        const product = await productDetail(productId);
        await this.guestMutate((lines) => {
          const existing = lines.find((l) => l.productId === productId);
          const desired = (existing?.quantity ?? 0) + quantity;
          const clamped = Math.min(desired, product.availableStock);
          if (clamped <= 0) {
            const e: ApiError = { code: 'OUT_OF_STOCK', message: 'Out of stock' };
            throw e;
          }
          if (existing) existing.quantity = clamped;
          else lines.push({ productId, quantity: clamped });
          return lines;
        });
      } else {
        const cart = await this.hydrateAuthedCart(
          await cartService.addItem(productId, quantity),
        );
        runInAction(() => this.setCart(cart));
      }
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.mutating = false;
      });
    }
  }

  async updateItem(productId: string, quantity: number): Promise<void> {
    this.mutating = true;
    this.error = null;
    try {
      if (this.mode === 'guest') {
        await this.guestMutate(async (lines) => {
          if (quantity <= 0) return lines.filter((l) => l.productId !== productId);
          const product = await productDetail(productId);
          const clamped = Math.min(quantity, product.availableStock);
          const existing = lines.find((l) => l.productId === productId);
          if (existing) existing.quantity = clamped;
          else lines.push({ productId, quantity: clamped });
          return lines;
        });
      } else {
        const cart = await this.hydrateAuthedCart(
          await cartService.updateItem(productId, quantity),
        );
        runInAction(() => this.setCart(cart));
      }
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.mutating = false;
      });
    }
  }

  async removeItem(productId: string): Promise<void> {
    this.mutating = true;
    this.error = null;
    try {
      if (this.mode === 'guest') {
        await this.guestMutate((lines) => lines.filter((l) => l.productId !== productId));
      } else {
        const cart = await this.hydrateAuthedCart(
          await cartService.removeItem(productId),
        );
        runInAction(() => this.setCart(cart));
      }
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.mutating = false;
      });
    }
  }

  async validate(): Promise<boolean> {
    this.validating = true;
    this.error = null;
    try {
      if (this.mode === 'guest') {
        const lines = readGuestLines();
        const products = await fetchProducts(lines.map((l) => l.productId));
        const conflicts: CartConflict[] = [];
        const validLines: GuestLine[] = [];
        for (const line of lines) {
          const product = products.get(line.productId);
          if (!product) {
            conflicts.push({
              productId: line.productId,
              reason: 'DELISTED',
              message: 'Product is no longer available',
            });
            continue;
          }
          if (product.stockStatus === 'OUT_OF_STOCK' || product.availableStock <= 0) {
            conflicts.push({
              productId: line.productId,
              reason: 'OUT_OF_STOCK',
              message: `${product.nameEn} is out of stock`,
            });
            continue;
          }
          if (line.quantity > product.availableStock) {
            conflicts.push({
              productId: line.productId,
              reason: 'INSUFFICIENT_STOCK',
              message: `Only ${product.availableStock} ${product.unit} available for ${product.nameEn}`,
              availableStock: product.availableStock,
            });
            validLines.push({ ...line, quantity: product.availableStock });
            continue;
          }
          if (product.moq && product.moq > 1 && line.quantity < product.moq) {
            conflicts.push({
              productId: line.productId,
              reason: 'BELOW_MOQ',
              message: `Minimum order quantity for ${product.nameEn} is ${product.moq}`,
              moq: product.moq,
            });
            validLines.push(line);
            continue;
          }
          validLines.push(line);
        }
        const cart = buildCartFromProducts(lines, products, this.identityProvider());
        runInAction(() => {
          this.setCart(cart);
          this.conflicts = conflicts;
        });
        return conflicts.length === 0;
      }
      const result = await cartService.validate();
      // Validate response shares the same bare CartItemDto shape as /cart, so
      // hydrate thumbnails / brand / spec back in (same fix as `load()`).
      const hydrated = await this.hydrateAuthedCart(result.cart);
      runInAction(() => {
        this.setCart(hydrated);
        this.conflicts = result.conflicts;
      });
      return result.valid;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      return false;
    } finally {
      runInAction(() => {
        this.validating = false;
      });
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.mode === 'guest') {
        writeGuestLines([]);
      } else {
        await cartService.clear();
      }
    } finally {
      // Always reset in-memory state, even if a backend delete rejected — the
      // caller (checkout) has already moved these lines into an order.
      runInAction(() => {
        this.setCart(EMPTY_CART);
        this.conflicts = [];
      });
    }
  }

  /** Wipe local guest cart and in-memory state without calling backend. */
  clearLocal(): void {
    writeGuestLines([]);
    this.setCart(EMPTY_CART);
    this.conflicts = [];
    this.error = null;
  }

  clearConflicts(): void {
    this.conflicts = [];
  }
}
